import Button from "components/common/Button";
import Checkbox from "components/common/Checkbox";
import SelectInput from "components/common/InputField/SelectInput";
import SelectInputWithSearch from "components/common/InputField/SelectInputWithSearch";
import TextInput from "components/common/InputField/TextInput";
import useGetBankAccounts from "hooks/integrationHooks/useGetBankAccounts";
import { useUpdateSalesSettings, useGetSalesSettings } from "hooks/salesHooks";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

export default function Sales() {
    const [syncSalesOrders, setSyncSalesOrders] = useState(false);
    const [formData, setFormData] = useState<{
        quickBooksOnline: {
            shouldAutoSyncSales: boolean;
            channelsToSync?: {
                channel: string;
                depositBankAccountRef: {
                    name: string;
                    id: string;
                };
            }[];
        };
    }>({
        quickBooksOnline: {
            shouldAutoSyncSales: false,
            channelsToSync: [],
        },
    });

    const { updatingSalesSettings, updateSalesSettingsSuccess, updateSettings, resetSuccess } =
        useUpdateSalesSettings();
    const { fetchingSalesSettings, fetchSalesSettingsSuccess, refetch } = useGetSalesSettings();
    const getBankAccountsQuery = useGetBankAccounts();

    console.log({ getBankAccountsQuery });

    const handleUpdate = () => {
        updateSettings({
            ...formData,
            quickBooksOnline: {
                ...formData.quickBooksOnline,
                shouldAutoSyncSales: syncSalesOrders,
                ...(syncSalesOrders
                    ? { channelsToSync: formData.quickBooksOnline.channelsToSync }
                    : {}),
            },
        });
    };

    useEffect(() => {
        if (!!fetchSalesSettingsSuccess) {
            const salesSettings = fetchSalesSettingsSuccess?.settings;
            const shouldAutoSync = salesSettings?.quickBooksOnline?.shouldAutoSyncSales;

            setSyncSalesOrders(shouldAutoSync);

            setFormData({
                quickBooksOnline: {
                    shouldAutoSyncSales: shouldAutoSync,
                    channelsToSync:
                        salesSettings?.quickBooksOnline?.channelsToSync?.map((item) => ({
                            channel: item?.channel,
                            depositBankAccountRef: {
                                name: item?.depositBankAccountRef?.name,
                                id: item?.depositBankAccountRef?.id,
                            },
                        })) || [],
                },
            });
        }
    }, [fetchSalesSettingsSuccess]);

    useEffect(() => {
        if (updateSalesSettingsSuccess) {
            refetch();
            resetSuccess();
            toast.success("Sales orders sync to QuickBooks successfully");
        }
    }, [updateSalesSettingsSuccess]);

    const handleChannelSync = (channel: string, value: string) => {
        setFormData((prev) => {
            const prevFormData = { ...prev };
            const prevChannelsToSync = prevFormData.quickBooksOnline.channelsToSync;
            const existingQuickbooksIndex = prevChannelsToSync.findIndex(
                (item) => item.channel === channel
            );

            if (existingQuickbooksIndex !== -1) {
                if (value === "None") {
                    prevChannelsToSync.splice(existingQuickbooksIndex, 1);
                } else {
                    prevChannelsToSync[existingQuickbooksIndex] = {
                        channel,
                        depositBankAccountRef: {
                            name:
                                getBankAccountsQuery?.data?.find((item) => item?.Id === value)
                                    ?.Name || "",
                            id: value,
                        },
                    };
                }
            } else {
                if (value === "None") {
                    return prev;
                }
                prevChannelsToSync.push({
                    channel,
                    depositBankAccountRef: {
                        name:
                            getBankAccountsQuery?.data?.find((item) => item?.Id === value)?.Name ||
                            "",
                        id: value,
                    },
                });
            }

            return {
                ...prev,
                quickBooksOnline: {
                    ...prev.quickBooksOnline,
                    channelsToSync: prevChannelsToSync,
                },
            };
        });
    };

    return (
        <div className="mt-[34px] max-w-[700px]">
            <div className="mt-[24px]">
                <p className="text-base font-rocGroteskMedium text-g-75">
                    Sync sales orders on Synth with QuickBooks online
                </p>

                <p className="text-[#64748B] text-base text-g-75">
                    When enabled, any sales orders created or updated in Synth will be automatically
                    created or updated in QuickBooks online in real time, no manual entry needed.
                </p>

                <div className="mt-[8px]">
                    <Checkbox
                        checked={syncSalesOrders}
                        onClick={(e) => {
                            const newValue = !syncSalesOrders;
                            setSyncSalesOrders(newValue);
                            setFormData((prev) => ({
                                ...prev,
                                quickBooksOnline: {
                                    ...prev.quickBooksOnline,
                                    shouldAutoSyncSales: newValue,
                                },
                            }));
                        }}
                        label={
                            <p className="text-gm-45 text-sm font-rocGroteskMedium">
                                Automatically sync sales orders to QuickBooks
                            </p>
                        }
                    />
                </div>
            </div>
            {syncSalesOrders && (
                <div className="mt-[24px]">
                    <div className="flex flex-col gap-4">
                        <SelectInputWithSearch
                            name="shopify"
                            label="Shopify"
                            placeholder="Select deposit bank account"
                            disabled={fetchingSalesSettings || updatingSalesSettings}
                            handleChange={(name, value) => {
                                handleChannelSync("Shopify", value);
                            }}
                            isRequired
                            dropdownOptions={[
                                ...getBankAccountsQuery?.data?.map((item) => ({
                                    label: item?.Name,
                                    subText: `Account ID: ${item?.Id}`,
                                    value: item?.Id,
                                })),
                                {
                                    label: "None",
                                    subText: "Don't sync any account",
                                    value: "None",
                                },
                            ]}
                            value={
                                formData?.quickBooksOnline?.channelsToSync?.find(
                                    (item) => item.channel === "Shopify"
                                )?.depositBankAccountRef?.id || "None"
                            }
                        />

                        <SelectInputWithSearch
                            name="amazon"
                            label="Amazon"
                            placeholder="Select deposit bank account"
                            disabled={fetchingSalesSettings || updatingSalesSettings}
                            handleChange={(name, value) => {
                                handleChannelSync("Amazon", value);
                            }}
                            isRequired
                            dropdownOptions={[
                                ...getBankAccountsQuery?.data?.map((item) => ({
                                    label: item?.Name,
                                    subText: `Account ID: ${item?.Id}`,
                                    value: item?.Id,
                                })),
                                {
                                    label: "None",
                                    subText: "Don't sync any account",
                                    value: "None",
                                },
                            ]}
                            value={
                                formData?.quickBooksOnline?.channelsToSync?.find(
                                    (item) => item.channel === "Amazon"
                                )?.depositBankAccountRef?.id || "None"
                            }
                        />

                        <SelectInputWithSearch
                            name="retail"
                            label="Retail"
                            placeholder="Select deposit bank account"
                            disabled={fetchingSalesSettings || updatingSalesSettings}
                            dropdownOptions={[
                                ...getBankAccountsQuery?.data?.map((item) => ({
                                    label: item?.Name,
                                    subText: `Account ID: ${item?.Id}`,
                                    value: item?.Id,
                                })),
                                {
                                    label: "None",
                                    subText: "Don't sync any account",
                                    value: "None",
                                },
                            ]}
                            isRequired
                            value={
                                formData?.quickBooksOnline?.channelsToSync?.find(
                                    (item) => item.channel === "Retail"
                                )?.depositBankAccountRef?.id || "None"
                            }
                            handleChange={(name, value) => {
                                handleChannelSync("Retail", value);
                            }}
                        />
                    </div>
                </div>
            )}

            <div className="flex justify-start absolute top-0 right-0">
                <Button
                    onClick={handleUpdate}
                    btnText={"Save Settings"}
                    type="button"
                    isLoading={updatingSalesSettings}
                    disabled={fetchingSalesSettings || updatingSalesSettings}
                    btnClassname="!w-fit !py-2"
                />
            </div>
        </div>
    );
}
